import React, { useState } from 'react';
import { X } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Category, CategoryUpdate } from '../../types/roadmap';

// Form data type without project_id (added by hook)
type CategoryFormData = Omit<import('../../types/roadmap').CategoryCreate, 'project_id'>;

interface CategoryFormProps {
  category?: Category | null;
  categories: Category[];
  onSave: (data: CategoryFormData | CategoryUpdate) => void;
  onCancel: () => void;
}

const AVAILABLE_ICONS = [
  'Zap', 'Cog', 'Users', 'Shield', 'Layers', 'Lock', 'Database',
  'Code', 'Settings', 'Target', 'Briefcase', 'FileText', 'GitBranch',
  'Box', 'Package', 'Cpu', 'Server', 'Cloud', 'Globe', 'Activity'
];

const COLOR_OPTIONS = [
  { name: 'Blue', value: 'from-blue-500 to-blue-600' },
  { name: 'Orange', value: 'from-orange-500 to-orange-600' },
  { name: 'Cyan', value: 'from-cyan-500 to-cyan-600' },
  { name: 'Green', value: 'from-green-500 to-green-600' },
  { name: 'Indigo', value: 'from-indigo-500 to-indigo-600' },
  { name: 'Pink', value: 'from-pink-500 to-pink-600' },
  { name: 'Purple', value: 'from-purple-500 to-purple-600' },
  { name: 'Red', value: 'from-red-500 to-red-600' },
  { name: 'Yellow', value: 'from-yellow-500 to-yellow-600' },
  { name: 'Teal', value: 'from-teal-500 to-teal-600' },
];

export const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  categories,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState(category?.title || '');
  const [iconName, setIconName] = useState(category?.icon_name || 'Box');
  const [color, setColor] = useState(category?.color || 'from-blue-500 to-blue-600');
  const [description, setDescription] = useState(category?.description || '');

  const isEdit = !!category;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      const updateData: CategoryUpdate = {
        title: title !== category.title ? title : undefined,
        icon_name: iconName !== category.icon_name ? iconName : undefined,
        color: color !== category.color ? color : undefined,
        description: description !== category.description ? description : undefined,
      };
      onSave(updateData);
    } else {
      const createData: CategoryFormData = {
        title,
        icon_name: iconName,
        color,
        description,
        display_order: categories.length, // Add at end
      };
      onSave(createData);
    }
  };

  const getIconComponent = (name: string) => {
    return (Icons as any)[name] || Icons.Box;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {isEdit ? 'Edit Category' : 'Add New Category'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., AI-Powered Application Features"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of this category"
              rows={3}
            />
          </div>

          {/* Icon Picker */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Icon
            </label>
            <div className="grid grid-cols-10 gap-2">
              {AVAILABLE_ICONS.map((icon) => {
                const IconComponent = getIconComponent(icon);
                const isSelected = iconName === icon;
                return (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setIconName(icon)}
                    className={`p-3 border-2 rounded-lg transition-all hover:scale-110 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    title={icon}
                  >
                    <IconComponent size={20} className={isSelected ? 'text-blue-600' : 'text-gray-600'} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Color
            </label>
            <div className="grid grid-cols-5 gap-3">
              {COLOR_OPTIONS.map((colorOption) => {
                const isSelected = color === colorOption.value;
                return (
                  <button
                    key={colorOption.value}
                    type="button"
                    onClick={() => setColor(colorOption.value)}
                    className={`p-4 rounded-lg transition-all hover:scale-105 ${
                      isSelected ? 'ring-4 ring-gray-400' : ''
                    }`}
                  >
                    <div className={`h-12 w-full rounded bg-gradient-to-r ${colorOption.value}`} />
                    <p className="text-xs text-gray-600 mt-1 text-center">{colorOption.name}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Preview
            </label>
            <div className={`bg-gradient-to-r ${color} text-white rounded-lg p-4 flex items-center space-x-3`}>
              {React.createElement(getIconComponent(iconName), { size: 24 })}
              <div>
                <div className="font-semibold">{title || 'Category Title'}</div>
                <div className="text-sm opacity-90">{description || 'Category description'}</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
            >
              {isEdit ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
